import { UTILITY_PROVIDER_OPTIONS } from "./incentiveRequirementsVector";
import {
  BUILDING_USE_OPTIONS,
  OWNER_CLASS_OPTIONS,
  OWNER_CLASS_LABELS,
  parseBuildingUses,
  serializeBuildingUses,
} from "../../../services/incentiveMatchingVocabulary";

/**
 * ADO 3960 - the property side of incentive matching.
 *
 * Common__PropertySettings is a free-form key/value store, so these keys have always
 * been settable here; nothing named them. IncentiveMatchingService.BuildPropertyProfile
 * reads exactly the keys below, and a typo saves happily and then silently never gates,
 * so the key field offers them as suggestions and the value field is typed per key.
 */
export type PropertySettingValueKind =
  | "triState"
  | "decimal"
  | "choice"
  | "buildingUses"
  | "text";

export interface IPropertySettingKeyDefinition {
  key: string;
  kind: PropertySettingValueKind;
  /** Shown under the row when the key is recognised. */
  description: string;
  /** Alternate spellings the API also accepts. */
  aliases?: string[];
  /** Suggested values for choice controls and the building use selector. */
  options?: string[];
  /** Presentation labels for choice values when a user is choosing one. */
  optionLabels?: Record<string, string>;
}

/** Values ParseTriState accepts, canonicalised to what we write. */
export const TRI_STATE_OPTIONS: { value: string; label: string }[] = [
  { value: "", label: "Unknown (not set)" },
  { value: "true", label: "Yes" },
  { value: "false", label: "No" },
];

export const PROPERTY_SETTING_KEYS: IPropertySettingKeyDefinition[] = [
  {
    key: "IncentivePlanState",
    kind: "choice",
    options: ["NY", "NJ"],
    description:
      "Overrides the property state for the requiredState gate. Falls back to the property address when unset.",
  },
  {
    key: "ElectricUtilityProvider",
    kind: "choice",
    options: [...UTILITY_PROVIDER_OPTIONS],
    description:
      "Electric provider for the utilityTerritories gate. Resolved from the ZIP when unset.",
  },
  {
    key: "GasUtilityProvider",
    kind: "choice",
    options: [...UTILITY_PROVIDER_OPTIONS],
    description:
      "Gas provider for the utilityTerritories gate. Resolved from the ZIP when unset.",
  },
  {
    key: "OwnerClass",
    kind: "choice",
    aliases: ["OwnershipType"],
    options: [...OWNER_CLASS_OPTIONS],
    optionLabels: OWNER_CLASS_LABELS,
    description:
      "Drives the eligible applicant class match. This is a direct fact and is separate from building use.",
  },
  {
    key: "BuildingUses",
    kind: "buildingUses",
    description:
      "Building uses present at the property. Mixed use is a standalone choice and does not add another use automatically.",
    options: BUILDING_USE_OPTIONS.map((option) => option.id),
  },
  {
    key: "IsAffordableHousing",
    kind: "triState",
    description:
      "requiresAffordableHousing gate. Unknown excludes the program in OE/PM and shows Low confidence in QS.",
  },
  {
    key: "IsHistoric",
    kind: "triState",
    description:
      "requiresHistoric gate. Five programmes also carry an authored YearBuilt " +
      "<= 1974 rule (IMPL-002 R7); the matcher itself no longer applies an age cutoff.",
  },
  {
    key: "IsPrimaryResidence",
    kind: "triState",
    description: "requiresHomeownership gate.",
  },
  {
    key: "IsBarn",
    kind: "triState",
    description: "requiresBarn gate.",
  },
  {
    key: "IsIncomeQualified",
    kind: "triState",
    aliases: ["IncomeQualified"],
    description: "requiresIncomeQualification gate.",
  },
  {
    key: "ProjectCapacityMw",
    kind: "decimal",
    description:
      "Project capacity in MW for the minimumCapacityMw gate. Read from PropertyVector when unset.",
  },
];

export const PROPERTY_SETTING_KEY_SUGGESTIONS: string[] = PROPERTY_SETTING_KEYS.map(
  (definition) => definition.key
);

/**
 * Recognition has to match the API exactly: GetSettingValue compares with
 * OrdinalIgnoreCase, so case differs freely but punctuation does not. Calling
 * is_historic "recognised" here would promise a gate the matcher never reads.
 */
const exactKey = (value?: string | null) => (value ?? "").trim().toLowerCase();

/** Looser form, for near-miss suggestions only. */
const loosenKey = (value?: string | null) =>
  exactKey(value).replace(/[^a-z0-9]+/g, "");

export const findPropertySettingKey = (
  key?: string | null
): IPropertySettingKeyDefinition | null => {
  const normalized = exactKey(key);
  if (!normalized) {
    return null;
  }

  return (
    PROPERTY_SETTING_KEYS.find(
      (definition) =>
        exactKey(definition.key) === normalized ||
        (definition.aliases ?? []).some(
          (alias) => exactKey(alias) === normalized
        )
    ) ?? null
  );
};

const editDistance = (left: string, right: string): number => {
  if (left === right) return 0;
  if (!left.length) return right.length;
  if (!right.length) return left.length;

  let previous = Array.from({ length: right.length + 1 }, (_, index) => index);
  for (let i = 1; i <= left.length; i += 1) {
    const current = [i];
    for (let j = 1; j <= right.length; j += 1) {
      current[j] = Math.min(
        previous[j] + 1,
        current[j - 1] + 1,
        previous[j - 1] + (left[i - 1] === right[j - 1] ? 0 : 1)
      );
    }
    previous = current;
  }

  return previous[right.length];
};

/**
 * A key that looks like a near miss on a matching key. Returns null for keys that are
 * recognised, empty, or clearly something else (Derapi:*, per-integration keys, ...).
 */
export const suggestPropertySettingKey = (key?: string | null): string | null => {
  const normalized = loosenKey(key);
  if (!normalized || findPropertySettingKey(key)) {
    return null;
  }

  let best: { key: string; distance: number } | null = null;
  for (const definition of PROPERTY_SETTING_KEYS) {
    const candidate = loosenKey(definition.key);
    const distance =
      candidate.startsWith(normalized) || normalized.startsWith(candidate)
        ? 1
        : editDistance(normalized, candidate);
    if (distance <= 3 && (!best || distance < best.distance)) {
      best = { key: definition.key, distance };
    }
  }

  return best?.key ?? null;
};

/** Canonicalises the tri-state spellings ParseTriState accepts onto true/false. */
export const normalizeTriStateValue = (value?: string | null): string => {
  const trimmed = (value ?? "").trim().toLowerCase();
  if (["true", "yes", "1", "y"].includes(trimmed)) {
    return "true";
  }
  if (["false", "no", "0", "n"].includes(trimmed)) {
    return "false";
  }
  return "";
};

export { parseBuildingUses, serializeBuildingUses };
